"use client";

import { useMemo } from "react";
import { FrontendApi } from "@ory/client";
import { useRuntimeConfig } from "@/app/shared/config/use-runtime-config";
import { getServerConfig } from "@/app/shared/config/config-getters";

/**
 * Create an Ory Kratos client with the given base path.
 * Use this factory function when you need a client with a specific URL.
 */
export function createOryClient(basePath: string): FrontendApi {
	return new FrontendApi({
		basePath,
		baseOptions: {
			withCredentials: true, // Enable cookies
		},
		isJsonMime: (mime: string) => /^application\/json(;.*)?$/i.test(mime),
	});
}

// Lazy-initialized singleton for server-side usage
let _oryClient: FrontendApi | null = null;

/**
 * Get the Ory Kratos client singleton for server-side usage.
 * For client components, use useOryClient() hook instead.
 */
export function getOryClient(): FrontendApi {
	if (!_oryClient) {
		const basePath =
			getServerConfig("NEXT_PUBLIC_AUTH_URL") ||
			process.env.NEXT_PUBLIC_AUTH_URL ||
			"";
		_oryClient = createOryClient(basePath);
	}
	return _oryClient;
}

/**
 * Hook to get an Ory client configured with the runtime auth URL.
 * Returns null while the runtime config is loading.
 *
 * @example
 * ```tsx
 * const ory = useOryClient();
 *
 * useEffect(() => {
 *   if (!ory) return;
 *   ory.toSession().then(...);
 * }, [ory]);
 * ```
 */
export function useOryClient(): FrontendApi | null {
	const { get } = useRuntimeConfig();
	const authUrl = get("NEXT_PUBLIC_AUTH_URL");

	return useMemo(() => {
		if (!authUrl) return null;
		return createOryClient(authUrl);
	}, [authUrl]);
}
